import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PlusIcon from '../../../assets/icons/plus.svg?react';
import { useReportRequestList } from '../../../hooks/queries/reportQueries';
import ReportRequestItem from './ReportRequestItem';
import Pagination from '../../../components/Pagination/Pagination';
import { SkeletonTable } from '../../../components/Skeleton';
import ReportDeleteModal from './ReportDeleteModal';
import ReportAppendModal from './ReportAppendModal';
import RefreshIcon from '../../../assets/icons/refresh.svg?react';
const ReportRequest = () => {
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  const [target, setTarget] = useState(null);
  const [appendModalOn, setAppendModalOn] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshMsg, setRefreshMsg] = useState('');
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'CreatedDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const keyword = searchParams.get('Keyword') || '';
  
  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder
    };
    
    // Keyword 파라미터 추가
    if (keyword) {
      params.Keyword = keyword;
    }
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword]);
  
  const { data: reports, isLoading, error, refetch } = useReportRequestList(queryParams);
  
  // 키워드 입력 핸들러
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };
  
  // 키워드 검색 핸들러 (Enter 키)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      const value = e.target.value.trim();
      if (value.length >= 2) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('Keyword', value);
          newParams.set('page', '1'); // 검색 시 첫 페이지로
          return newParams;
        });
      } else if (value.length === 0) {
        // 빈 값이면 Keyword 파라미터 제거
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('Keyword');
          newParams.set('page', '1');
          return newParams;
        });
      }
    }
  };

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const totalPages = reports?.Total ? Math.ceil(reports.Total / pageSize) : 1;

  const handleDeleteSuccess = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
    setDeleteModalOn(false);
    refetch();
  };

  const handleDelete = (report) => {
    setTarget(report);
    setDeleteModalOn(true);
  };

  const handleAppendSuccess = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
    setAppendModalOn(false);
    refetch();
  };


  const handleRefresh = async () => {
    try {
      setRefreshMsg('...');
      await refetch();
      setRefreshMsg('보고서 목록 새로고침 완료!');
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    } catch (error) {
      console.error('보고서 목록 새로고침 실패:', error);
      setRefreshMsg('보고서 목록 새로고침 실패!');
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    }
  };

  return (
    <>
      <div className="flex justify-between align-center mb-10">
        <div className="flex w-half">
          <input 
            type="text" 
            placeholder="키워드 검색 (2자 이상) - 보고서명, 템플릿, 버전" 
            className="input-primary flex-1 text-b2-400" 
            value={keywordInput}
            onChange={handleKeywordChange}
            onKeyDown={handleKeywordKeyDown}
          />
        </div>
        <div className="flex justify-end align-center gap-5">
          <p className="text-b2-400 text-primary">{refreshMsg}</p>
          <button className="btn big-btn btn-style-5 flex align-center gap-3" onClick={handleRefresh}>
            <RefreshIcon color='var(--gray-600)'/>
            <p className="text-b2-400 text-gray-600">새로고침</p>
          </button>
          <button className="btn big-btn btn-primary flex align-center gap-3" onClick={() => setAppendModalOn(true)}>
            <PlusIcon color='var(--white)'/>
            <p className="text-b2-400 text-white">보고서 생성</p>
          </button>
        </div>
      </div>
      {isLoading ? (
        <SkeletonTable rows={5} cols={6} />
      ) : error ? (
        <div className="error-message">데이터를 불러오는데 실패했습니다</div>
      ) : (
        <div className="primary-table">
          <table>
            <thead>
              <tr>
                <th>보고서명</th>
                <th>요청시간</th>
                <th>종료시간</th>
                <th>템플릿</th>
                <th>상태</th>
                <th width="200px"></th>
              </tr>
            </thead>
            <tbody>
              {reports?.Items?.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    <p className="text-b3-700 text-gray-500">보고서 데이터가 없습니다.</p>
                  </td>
                </tr>
              ) : (
                reports?.Items?.map(report => (
                  <ReportRequestItem key={report.ReportRequestSysId} report={report} onDeleteSuccess={handleDeleteSuccess} onDelete={handleDelete} />
                ))
              )}
            </tbody>
          </table>
          <div className="pagination-wrapper">
            <Pagination
            currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              />
          </div>
        </div>
      )}
      {deleteModalOn && (
        <ReportDeleteModal
          onClose={() => setDeleteModalOn(false)}
          onDeleteSuccess={handleDeleteSuccess}
          report={target}
        />
      )}
      {appendModalOn && (
        <ReportAppendModal
          onClose={() => setAppendModalOn(false)}
          onSuccess={handleAppendSuccess}
        />
      )}
    </>
  )
}

export default ReportRequest